import type { ApiAccessToken } from '../../../auth/getApiAccessToken'
import {
  LIVE_MESSAGE,
  type LiveEmbedInboundMessage,
  type LiveEmbedState,
  type LiveEmbedTokenMessage,
  isLiveEmbedInboundMessage,
} from './liveEmbedProtocol'

export const LIVE_READY_TIMEOUT_MS = 15_000

interface LiveEmbedHostOptions {
  iframe: HTMLIFrameElement
  /** Base URL of the live app (VITE_LIVE_URL); only its origin is trusted. */
  liveUrl: string
  getToken: () => Promise<ApiAccessToken | null>
  onState: (state: LiveEmbedState) => void
  readyTimeoutMs?: number
  win?: Window
}

/**
 * Serves one live dashboards iframe: answers its token requests and relays its status.
 * Only messages from the live app's origin AND from this iframe's window are accepted; the token is
 * only ever posted to the live app's origin. Returns a dispose function.
 */
export function createLiveEmbedHost({
  iframe,
  liveUrl,
  getToken,
  onState,
  readyTimeoutMs = LIVE_READY_TIMEOUT_MS,
  win = window,
}: LiveEmbedHostOptions): () => void {
  const liveOrigin = new URL(liveUrl).origin
  let disposed = false
  let ready = false

  const setState = (state: LiveEmbedState) => {
    if (!disposed) onState(state)
  }
  const unavailable = () => setState({ status: 'unavailable', since: null })

  const sendToken = async () => {
    try {
      const token = await getToken()
      if (disposed) return
      if (!token) return unavailable()
      const message: LiveEmbedTokenMessage = {
        type: LIVE_MESSAGE.token,
        token: token.accessToken,
        expiresOn: token.expiresOn,
      }
      iframe.contentWindow?.postMessage(message, liveOrigin)
    } catch {
      unavailable()
    }
  }

  const handle = (message: LiveEmbedInboundMessage) => {
    switch (message.type) {
      case LIVE_MESSAGE.ready:
        ready = true
        void sendToken()
        break
      case LIVE_MESSAGE.tokenRequest:
        void sendToken()
        break
      case LIVE_MESSAGE.status:
        setState({ status: message.online ? 'online' : 'offline', since: message.online ? null : message.since })
        break
    }
  }

  const onMessage = (event: MessageEvent) => {
    if (event.origin !== liveOrigin) return
    if (!iframe.contentWindow || event.source !== iframe.contentWindow) return
    if (!isLiveEmbedInboundMessage(event.data)) return
    handle(event.data)
  }

  setState({ status: 'loading', since: null })
  win.addEventListener('message', onMessage)
  const readyTimer = win.setTimeout(() => {
    if (!ready) unavailable()
  }, readyTimeoutMs)

  return () => {
    disposed = true
    win.removeEventListener('message', onMessage)
    win.clearTimeout(readyTimer)
  }
}
